import type React from "react";
import { useEffect, useMemo, useState } from "react";
import {
    PROMOTION_DISALLOWED_PLATFORMS,
    type ChoosePlatformsProps,
    type SocialsConnected,
} from "./types";
import { Label } from "../misc/Label";
import { FaCheck } from "react-icons/fa6";
import { getAuthHeader } from "../../utils/authHeader";
import { getPlatformIcon } from "../../utils/logo-getter";
import axios from "axios";
import { toast } from "react-toastify";
import CustomToast from "../misc/CustomToast";
import { Button } from "../misc/Button";
import { Card } from "../misc/Card";
import { Avatar, AvatarImage, AvatarFallback } from "../misc/Avatar";
import LoadingScreen from "../../screens/LoadingScreen";
import { useNavigate } from "react-router-dom";
import { useSocket } from "../../context/SocketContext";
import type { SocialConnectionsData } from "../settings/types";
import { FaUserAlt } from "react-icons/fa";
import { ImageIcon, Video } from "lucide-react";
import { useIsMobile } from "../../hooks/useIsMobile";

import {
    PLATFORM_CAPABILITY,
    computeQuickSelection,
    type MediaSupport,
    type QuickSelectType,
} from "./quickSelectPlatforms";

// Which media-type pills are active for each support level
const SUPPORT_PILLS: Record<MediaSupport, { image: boolean; video: boolean }> = {
    image: { image: true, video: false },
    video: { image: false, video: true },
    flex:  { image: true, video: true },
};

const MediaPills: React.FC<{ support: MediaSupport; isCardSelected: boolean }> = ({ support }) => {
    const pills = SUPPORT_PILLS[support];
    const activeCls = "bg-[#E9C468] text-[#193456] border border-[#E9C468]";
    const inactiveCls = "bg-[#D3D1C7] text-[#5F5E5A] border border-[#D3D1C7]";

    return (
        <div className="flex items-center gap-1 flex-shrink-0">
            <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full ${pills.image ? activeCls : inactiveCls}`}>
                <ImageIcon className="w-3 h-3" />
                Image
            </span>
            <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full ${pills.video ? activeCls : inactiveCls}`}>
                <Video className="w-3 h-3" />
                Video
            </span>
        </div>
    );
};

interface DisplayNameData {
    platform: string;
    userImage: string;
    displayName: string;
    pageName?: string;
    username?: string;
    profileUrl?: string;
}

const ChoosePlatforms: React.FC<ChoosePlatformsProps> = ({
    setCurrentStep,
    onNext,
    selectedPlatforms,
    setSelectedPlatforms,
    from,
    listingId,
    listingType,
    instagramDisabled,
    isVideo: _isVideo = false,
    selectedListing,
    youtubeDisabled,
    hasMedia: _hasMedia,
    enableMediaCapability = false,
    isAdHoc = false,
    onMediaTypePreset,
    remainingPostLimit,
    onYoutubeProfileUrl,
    onConnectionData,
}) => {
    const navigate = useNavigate();
    // MB-1455: the weekly limit blocks ADDING Instagram, not removing it.
    // Without this an edit-mode campaign on an account already at the cap can
    // neither pass the schedule step nor drop the platform.
    const instagramLocked = instagramDisabled && !selectedPlatforms.instagram;
    const { socket } = useSocket(); // Destructure socket from the context
    const isMobile = useIsMobile(900);

    const [connections, setConnections] = useState<SocialsConnected>({
        facebook: false,
        instagram: false,
        twitter: false,
        linkedin: false,
        pinterest: false,
        google: false,
        youtube: false,
    });

    const [connectionData, setConnectionData] = useState<
        SocialConnectionsData[]
    >([]);

    const [loading, setLoading] = useState<boolean>(true);

    

    const socialPlatforms = [
        {
            id: "facebook",
            name: "Facebook",
            icon: getPlatformIcon("facebook"),
            color: "bg-[#1877F2] text-white",
        },
        {
            id: "instagram",
            name: "Instagram",
            icon: getPlatformIcon("instagram"),
            color: "bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] text-white",
        },
        {
            id: "twitter",
            name: "X / Twitter",
            icon: getPlatformIcon("twitter"),
            color: "bg-[#000000] text-white",
        },
        {
            id: "linkedin",
            name: "LinkedIn",
            icon: getPlatformIcon("linkedin"),
            color: "bg-[#0077B5] text-white",
        },
        {
            id: "pinterest",
            name: "Pinterest",
            icon: getPlatformIcon("pinterest"),
            color: "bg-[#CB1F27] text-white",
        },
        {
            id: "google",
            name: "Google My Business",
            icon: getPlatformIcon("google"),
            color: "bg-[#4285F4] text-white",
        },
        {
            id: "youtube",
            name: "YouTube",
            icon: getPlatformIcon("youtube"),
            color: "bg-[#ff0033] text-white",
        },
    ];

    useEffect(() => {
        const fetchConnections = async () => {
            setLoading(true);
            try {
                const headers = await getAuthHeader();
                const backendUrl = process.env.REACT_APP_BACKEND_URL;

                const response = await axios.get(
                    `${backendUrl}/socials/profiles`,
                    { headers }
                );

                const responseData = response.data.data || response.data;

                const activeSocialAccounts =
                    responseData.activeSocialAccounts || [];

                // Transform displayNames array to object
                const allConnectionDataArray = responseData.displayNames || [];
                const allConnectionData = allConnectionDataArray.reduce(
                    (
                        acc: Record<string, DisplayNameData>,
                        curr: DisplayNameData
                    ) => {
                        acc[curr.platform.toLowerCase()] = curr;
                        return acc;
                    },
                    {}
                );

                const fetchedConnections = [
                    {
                        platform: "facebook",
                        connected: activeSocialAccounts.includes("facebook"),
                        userData: allConnectionData["facebook"] || {
                            userImage: "",
                            displayName: "",
                            pageName: "",
                        },
                    },
                    {
                        platform: "instagram",
                        connected: activeSocialAccounts.includes("instagram"),
                        userData: allConnectionData["instagram"] || {
                            userImage: "",
                            displayName: "",
                            username: "",
                        },
                    },
                    {
                        platform: "twitter",
                        connected: activeSocialAccounts.includes("twitter"),
                        userData: allConnectionData["twitter"] || {
                            userImage: "",
                            displayName: "",
                            username: "",
                        },
                    },
                    {
                        platform: "linkedin",
                        connected: activeSocialAccounts.includes("linkedin"),
                        userData: allConnectionData["linkedin"] || {
                            userImage: "",
                            displayName: "",
                            username: "",
                        },
                    },

                    {
                        platform: "pinterest",
                        connected: activeSocialAccounts.includes("pinterest"),
                        userData: allConnectionData["pinterest"] || {
                            userImage: "",
                            displayName: "",
                            username: "",
                        },
                    },
                    {
                        platform: "google",
                        connected: activeSocialAccounts.includes("gmb"),
                        userData: allConnectionData["gmb"] || {
                            userImage: "",
                            displayName: "",
                            username: "",
                        },
                    },
                    {
                        platform: "youtube",
                        connected: activeSocialAccounts.includes("youtube"),
                        userData: allConnectionData["youtube"] || {
                            userImage: "",
                            displayName: "",
                            username: "",
                        },
                    },
                ];

                setConnections({
                    facebook: activeSocialAccounts.includes("facebook"),
                    instagram: activeSocialAccounts.includes("instagram"),
                    twitter: activeSocialAccounts.includes("twitter"),
                    linkedin: activeSocialAccounts.includes("linkedin"),
                    pinterest: activeSocialAccounts.includes("pinterest"),
                    google: activeSocialAccounts.includes("gmb"),
                    youtube: activeSocialAccounts.includes("youtube")
                });
                setConnectionData(fetchedConnections);

                if (onConnectionData) {
                    const profiles: Record<string, string> = {};
                    fetchedConnections.forEach((c) => {
                        profiles[c.platform] = c.userData.userImage || "";
                    });
                    onConnectionData(profiles);
                }

                const youtubeProfileUrl = allConnectionData["youtube"]?.profileUrl;
                if (youtubeProfileUrl && onYoutubeProfileUrl) {
                    onYoutubeProfileUrl(youtubeProfileUrl);
                }
            } catch (error) {
                console.error("Fetch Connections Error:", error);
                let errorMessage =
                    "Error fetching social profiles. Please try again later.";
                if (axios.isAxiosError(error) && error.response) {
                    errorMessage += ` Server responded with status ${error.response.status}.`;
                }

                toast.error(
                    <CustomToast message={errorMessage} type="error" />,
                    {
                        autoClose: 3000,
                    }
                );

                // Redirect to the previous page
                setTimeout(() => {
                    navigate(from || "/");
                }, 300);
            } finally {
                setLoading(false);
            }
        };

        fetchConnections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate, from]);

    // Integrate Socket Connection
    useEffect(() => {
        if (!socket) return;

        // Join the same room as SocialConnections
        socket.emit("joinSocialConnectionsRoom");

        // Handler for socialsUpdate event
        const handleSocialsUpdate = (
            updatedConnections: SocialConnectionsData[]
        ) => {
            const defaultUserData: Record<
                string,
                SocialConnectionsData["userData"]
            > = {
                facebook: {
                    userImage: "",
                    displayName: "",
                    pageName: "",
                },
                instagram: {
                    userImage: "",
                    displayName: "",
                    username: "",
                },
                twitter: {
                    userImage: "",
                    displayName: "",
                    username: "",
                },
                linkedin: {
                    userImage: "",
                    displayName: "",
                    username: "",
                },
                youtube: {
                    userImage: "",
                    displayName: "",
                },
                pinterest: {
                    userImage: "",
                    displayName: "",
                    username: "",
                },
                google: {
                    userImage: "",
                    displayName: "",
                    username: "",
                },
            };

            const supportedPlatforms = [
                "facebook",
                "instagram",
                "twitter",
                "linkedin",
                "pinterest",
                "google",
                "youtube",
            ];

            const mergedConnections: SocialsConnected = {
                facebook: false,
                instagram: false,
                twitter: false,
                linkedin: false,
                pinterest: false,
                google: false,
                youtube: false,
            };

            const updatedConnectionData: SocialConnectionsData[] = [];

            supportedPlatforms.forEach((platform) => {
                const updated = updatedConnections.find((conn) => {
                    // The api reports Google Business as "gmb" (same mapping as
                    // the initial /socials/profiles fetch above).
                    const key =
                        conn.platform.toLowerCase() === "gmb"
                            ? "google"
                            : conn.platform.toLowerCase();
                    return key === platform.toLowerCase();
                });

                if (updated) {
                    mergedConnections[platform as keyof SocialsConnected] =
                        updated.connected;

                    // If userData exists, use it; otherwise, use default
                    const userData =
                        updated.userData || defaultUserData[platform];
                    updatedConnectionData.push({
                        platform,
                        connected: updated.connected,
                        userData,
                    });
                } else {
                    // Platform not present in updatedConnections; use default values
                    mergedConnections[platform as keyof SocialsConnected] =
                        false;
                    updatedConnectionData.push({
                        platform,
                        connected: false,
                        userData: defaultUserData[platform],
                    });
                }
            });

            setConnections(mergedConnections);
            setConnectionData(updatedConnectionData);
            setLoading(false);
        };

        socket.on("socialsUpdate", handleSocialsUpdate);

        socket.on("connect_error", (err: any) => {
            console.error("WebSocket connection error:", err);
        });

        return () => {
            socket.off("socialsUpdate", handleSocialsUpdate);
        };
    }, [socket]);

    const handlePlatformToggle = (platformId: keyof SocialsConnected) => {
        if (platformId === "instagram" && instagramLocked) {
            toast.error(
                <CustomToast
                    message="Instagram posting frequency has reached its weekly limit. To avoid account suspension, you cannot add another Instagram campaign at this time."
                    type="error"
                />,
                { autoClose: 5000 }
            );
            return;
        }
        setSelectedPlatforms({
            ...selectedPlatforms,
            [platformId]: !selectedPlatforms[platformId],
        });
    };

    const handleNextStep = () => {
        // Check if at least one platform is selected and all selected platforms are connected
        const hasSelectedPlatforms = Object.values(selectedPlatforms).some(
            (isSelected) => isSelected
        );

        const hasUnconnectedSelectedPlatforms = Object.keys(
            selectedPlatforms
        ).some(
            (platformId) =>
                selectedPlatforms[platformId as keyof SocialsConnected] &&
                !connections[platformId as keyof SocialsConnected]
        );

        if (!hasSelectedPlatforms) {
            toast.error(
                <CustomToast
                    message="Please select at least one social platform."
                    type="error"
                />,
                {
                    autoClose: 3000,
                }
            );
            return;
        }

        if (hasUnconnectedSelectedPlatforms) {
            toast.error(
                <CustomToast
                    message="Please connect all selected social platforms."
                    type="error"
                />,
                {
                    autoClose: 3000,
                }
            );
            return;
        }

        if (onNext) {
            onNext();
            return;
        }
        setCurrentStep && setCurrentStep(2);
    };

    const handleBackStep = () => {
        sessionStorage.removeItem("from");
        sessionStorage.removeItem("listingId");
        sessionStorage.removeItem("listingType");
        sessionStorage.removeItem("parsedFooter");

        if (from) {
            navigate(from, { state: { listingId: listingId } });
        } else {
            navigate("/");
        }
    };

    // Check if any selected platform is not connected
    const hasUnconnectedSelectedPlatforms = Object.keys(selectedPlatforms).some(
        (platformId) =>
            selectedPlatforms[platformId as keyof SocialsConnected] &&
            !connections[platformId as keyof SocialsConnected]
    );

    // Disable Next button if any selected platform is not connected or no platform is selected
    const selectedPlatformNum = Object.values(selectedPlatforms).filter(
        (isSelected) => isSelected
    ).length;

    const isNextDisabled =
        hasUnconnectedSelectedPlatforms || selectedPlatformNum <= 0;

    // Always show all platforms; incompatible ones are marked "Not Available" below
    const filteredConnectionData = connectionData;

    // Derive a media constraint from the currently selected platforms so that
    // image-only and video-only platforms can't be combined.
    const mediaConstraint = useMemo<"image" | "video" | "flex">(() => {
        if (!enableMediaCapability) return "flex";
        const selectedIds = (Object.keys(selectedPlatforms) as Array<keyof SocialsConnected>).filter(
            (id) => selectedPlatforms[id]
        );
        if (selectedIds.some((id) => PLATFORM_CAPABILITY[id]?.support === "video")) return "video";
        if (selectedIds.some((id) => PLATFORM_CAPABILITY[id]?.support === "image")) return "image";
        return "flex";
    }, [selectedPlatforms, enableMediaCapability]);

    // "All video" / "All images" one-click presets (MB-1492). Ad-hoc flows only:
    // campaign, promotion, lead-gen, text-only and video-showcase flows have
    // bespoke platform rules and keep manual selection.
    const listingTypeKey = selectedListing?.listingType
        ?.toLowerCase()
        .replace(/\s/g, "");
    const showQuickSelect =
        isAdHoc &&
        enableMediaCapability &&
        !_isVideo &&
        listingTypeKey !== "textonly" &&
        listingTypeKey !== "leadgeneration" &&
        listingTypeKey !== "promotion";

    const handleQuickSelect = (type: QuickSelectType) => {
        const { selection } = computeQuickSelection(type, connectionData);
        // Respect the per-card selection guards; no current chip-rendering
        // caller passes these, but a preset must never select what a card
        // click would refuse.
        if (instagramDisabled && !selectedPlatforms.instagram) {
            selection.instagram = false;
        }
        if (youtubeDisabled) {
            selection.youtube = false;
        }
        const count = Object.values(selection).filter(Boolean).length;
        if (count === 0) {
            toast.error(
                <CustomToast
                    message={
                        type === "video"
                            ? "No connected platforms support video posts."
                            : "No connected platforms support image posts."
                    }
                    type="error"
                />,
                { autoClose: 3000, toastId: `quick-select-empty-${type}` }
            );
            return;
        }
        // Guard the click instead of funneling into the plan-limit modal at
        // Next (each selected platform consumes one post from the plan).
        if (remainingPostLimit !== undefined && count > remainingPostLimit) {
            toast.error(
                <CustomToast
                    message={`Your plan has ${remainingPostLimit} post${
                        remainingPostLimit === 1 ? "" : "s"
                    } left this month. Select up to ${remainingPostLimit} platform${
                        remainingPostLimit === 1 ? "" : "s"
                    }.`}
                    type="error"
                />,
                { autoClose: 4000, toastId: "quick-select-plan-limit" }
            );
            return;
        }
        setSelectedPlatforms(selection);
        onMediaTypePreset?.(type);
    };

    return (
        <div className="flex flex-col justify-between h-full">
            <Label className="font-bold text-xl text-primary mb-4">
                Choose Your Social Platforms
            </Label>

            {!loading && showQuickSelect && (
                <div className="flex flex-wrap items-center gap-2.5 mb-5">
                    <span className="text-sm font-semibold text-gray-500">
                        Quick select:
                    </span>
                    <button
                        type="button"
                        onClick={() => handleQuickSelect("video")}
                        className="inline-flex items-center gap-1.5 rounded-full border border-gray-300 bg-white px-3 py-1 text-xs font-bold text-primary transition-colors hover:bg-lightSecondary"
                    >
                        <Video className="w-3.5 h-3.5" />
                        All video
                    </button>
                    <button
                        type="button"
                        onClick={() => handleQuickSelect("image")}
                        className="inline-flex items-center gap-1.5 rounded-full border border-gray-300 bg-white px-3 py-1 text-xs font-bold text-primary transition-colors hover:bg-lightSecondary"
                    >
                        <ImageIcon className="w-3.5 h-3.5" />
                        All images
                    </button>
                    <span className="text-xs text-gray-500">
                        Selects every connected platform that supports it - adjust below anytime.
                    </span>
                </div>
            )}

            {loading ? (
                <LoadingScreen />
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {/*awaitingConnection && (
                        <div className="flex items-center justify-center space-x-2">
                            <span className="text-primary">
                                Waiting for connection
                            </span>
                            <BiDotsHorizontalRounded
                                className="animate-ping text-secondary"
                                size={25}
                            />
                        </div>
                    )*/}
                    {filteredConnectionData.map((connection) => {
                        const platformId =
                            connection.platform as keyof SocialsConnected;
                        const isSelected = selectedPlatforms[platformId];
                        const isConnected = connection.connected;
                        const userData = connection.userData;
                        const isYoutubeDisabled = platformId === "youtube" && youtubeDisabled;
                        const isTextOnlyFlow =
                            selectedListing?.listingType
                                ?.toLowerCase()
                                .replace(/\s/g, "") === "textonly";
                        const isLeadGenFlow =
                            selectedListing?.listingType
                                ?.toLowerCase()
                                .replace(/\s/g, "") === "leadgeneration";
                        const TEXT_ONLY_SUPPORTED = ["facebook", "twitter", "linkedin"];

                        // When capability mode is on, image-only and video-only platforms can't be combined.
                        const capability = PLATFORM_CAPABILITY[connection.platform];
                        const isCapabilityConflict =
                            enableMediaCapability &&
                            !isSelected &&
                            ((mediaConstraint === "video" && capability?.support === "image") ||
                                (mediaConstraint === "image" && capability?.support === "video"));

                        const isNotAvailable =
                            isCapabilityConflict ||
                            (_isVideo &&
                                (connection.platform === "pinterest" ||
                                    connection.platform === "google")) ||
                            (listingType?.toLowerCase() === "promotion" &&
                                PROMOTION_DISALLOWED_PLATFORMS.includes(
                                    connection.platform
                                )) ||
                            (selectedListing?.listingType
                                ?.toLowerCase()
                                .replace(/\s/g, "") === "leadgeneration" &&
                                (connection.platform === "google" || connection.platform === "youtube")) ||
                            (isTextOnlyFlow &&
                                !TEXT_ONLY_SUPPORTED.includes(connection.platform));

                        if (isNotAvailable) {
                            return (
                                <Card
                                    key={`${connection.platform}-${userData.displayName}-unavailable`}
                                    className="p-4 flex flex-col items-center cursor-not-allowed bg-white text-primary rounded-lg shadow-md transition-colors duration-300 relative"
                                >
                                    {/* Dimming overlay — sits above content but below the pill */}
                                    <div className="absolute inset-0 bg-white/60 rounded-lg z-10" />

                                    <div className="flex flex-col items-center w-full">
                                        <div className="flex items-center justify-center mb-2 rounded-lg bg-white p-1">
                                            {
                                                socialPlatforms.find(
                                                    (p) =>
                                                        p.id ===
                                                        connection.platform
                                                )?.icon
                                            }
                                        </div>
                                        <h3 className="text-lg font-semibold">
                                            {
                                                socialPlatforms.find(
                                                    (p) =>
                                                        p.id ===
                                                        connection.platform
                                                )?.name
                                            }
                                        </h3>
                                        {isConnected ? (
                                            <div className="mt-2 w-full">
                                                <div
                                                    key={`${connection.platform}-${userData.displayName}`}
                                                    className={`flex gap-2 mb-2 ${isMobile ? "flex-col" : "flex-row items-end"}`}
                                                >
                                                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                                                        <Avatar>
                                                            {userData.userImage ? (
                                                                <AvatarImage
                                                                    src={userData.userImage}
                                                                    alt={userData.displayName}
                                                                />
                                                            ) : (
                                                                <>
                                                                    {userData?.displayName ? (
                                                                        <AvatarFallback>
                                                                            {userData.displayName.charAt(0)}
                                                                            {userData.displayName.split(" ")[1]?.charAt(0)}
                                                                        </AvatarFallback>
                                                                    ) : (
                                                                        <div className="bg-white rounded-full flex items-center justify-center border w-10 h-10">
                                                                            <FaUserAlt className="text-secondary w-5 h-5" />
                                                                        </div>
                                                                    )}
                                                                </>
                                                            )}
                                                        </Avatar>
                                                        <div>
                                                            <div className="font-semibold">
                                                                {userData.displayName}
                                                            </div>
                                                            {connection.platform === "facebook" ? (
                                                                <div className="text-xs text-muted-foreground">
                                                                    Page Name: {userData.pageName}
                                                                </div>
                                                            ) : userData?.username && userData?.username.length > 0 ? (
                                                                <div className="text-xs text-muted-foreground">
                                                                    Username: {userData.username}
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                    <div className={`flex ${isMobile ? "justify-center" : "justify-end self-end ml-auto"}`}>
                                                        {isAdHoc && !isTextOnlyFlow && !isLeadGenFlow && capability ? (
                                                            <MediaPills support={capability.support} isCardSelected={false} />
                                                        ) : (
                                                            <span className="relative z-20 bg-[#E9C468] text-[#193456] text-xs font-bold px-2 py-1 rounded-full shadow-sm whitespace-nowrap">
                                                                Not Available
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <p className="text-red-600 text-sm mt-1">
                                                Not Connected
                                            </p>
                                        )}
                                    </div>
                                </Card>
                            );
                        }

                        return (
                          <Card
                            key={`${connection.platform}-${userData.displayName}`}
                            className={`p-4 flex flex-col items-center ${
                              (platformId === "instagram" &&
                                instagramLocked) ||
                              isYoutubeDisabled
                                ? "cursor-not-allowed"
                                : "cursor-pointer"
                            } ${
                              isSelected
                                ? `${
                                    socialPlatforms.find(
                                      (p) => p.id === connection.platform,
                                    )?.color
                                  }`
                                : "bg-white text-primary"
                            } rounded-lg shadow-md transition-colors duration-300 relative`}
                            onClick={() => {
                              if (
                                platformId === "instagram" &&
                                instagramLocked
                              ) {
                                toast.error(
                                  <CustomToast
                                    message="Instagram posting frequency has reached its weekly limit. To avoid account suspension, you cannot add another Instagram campaign at this time."
                                    type="error"
                                  />,
                                  { autoClose: 5000 },
                                );
                                return;
                              }
                              if (isYoutubeDisabled) {
                                toast.error(
                                  <CustomToast
                                    message="Please add a video in listing to enable YouTube campaigns."
                                    type="error"
                                  />,
                                  { autoClose: 5000 },
                                );
                                return;
                              }
                              handlePlatformToggle(platformId);
                            }}
                          >
                            {platformId === "instagram" && instagramLocked && (
                              <div className="absolute inset-0 bg-white/60 rounded-lg z-10" />
                            )}
                            {isYoutubeDisabled && (
                              <div className="absolute inset-0 bg-white/60 rounded-lg z-10" />
                            )}

                            {isSelected && isConnected && (
                              <FaCheck className="absolute top-2 right-2 text-green-500 h-5 w-5" />
                            )}

                            <div className="flex flex-col items-center w-full">
                              <div className="flex items-center justify-center mb-2 rounded-lg bg-white p-1">
                                {
                                  socialPlatforms.find(
                                    (p) => p.id === connection.platform,
                                  )?.icon
                                }
                              </div>
                              <h3 className="text-lg font-semibold">
                                {
                                  socialPlatforms.find(
                                    (p) => p.id === connection.platform,
                                  )?.name
                                }
                              </h3>
                              {isConnected ? (
                                <div className="mt-2 w-full">
                                  <div
                                    key={`${connection.platform}-${userData.displayName}`}
                                    className={`flex gap-2 mb-2 ${isMobile ? "flex-col" : "flex-row items-end"}`}
                                  >
                                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                                      <Avatar>
                                        {userData.userImage ? (
                                          <AvatarImage
                                            src={userData.userImage}
                                            alt={userData.displayName}
                                          />
                                        ) : (
                                          <>
                                            {userData?.displayName ? (
                                              <AvatarFallback>
                                                {userData.displayName.charAt(0)}
                                                {userData.displayName
                                                  .split(" ")[1]
                                                  ?.charAt(0)}
                                              </AvatarFallback>
                                            ) : (
                                              <div className="bg-white rounded-full flex items-center justify-center border w-10 h-10">
                                                <FaUserAlt className="text-secondary w-5 h-5" />
                                              </div>
                                            )}
                                          </>
                                        )}
                                      </Avatar>
                                      <div>
                                        <div className="font-semibold">
                                          {userData.displayName}
                                        </div>

                                        {connection.platform === "facebook" &&
                                        userData.pageName ? (
                                          <div className="text-xs text-muted-foreground">
                                            Page Name: {userData.pageName}
                                          </div>
                                        ) : connection.platform === "youtube" &&
                                          userData.displayName ? (
                                          <div className="text-xs text-muted-foreground">
                                            Channel Name: {userData.displayName}
                                          </div>
                                        ) : userData?.username &&
                                          userData?.username.length > 0 ? (
                                          <div className="text-xs text-muted-foreground">
                                            Username: {userData.username}
                                          </div>
                                        ) : null}
                                      </div>
                                    </div>
                                    <div className={`flex ${isMobile ? "justify-center" : "justify-end self-end ml-auto"}`}>
                                      {platformId === "instagram" && instagramLocked ? (
                                        <span className="relative z-20 bg-[#E9C468] text-[#193456] text-xs font-bold px-2 py-1 rounded-full shadow-sm whitespace-nowrap">
                                          Weekly Limit Reached
                                        </span>
                                      ) : isYoutubeDisabled ? (
                                        <span className="relative z-20 bg-[#E9C468] text-[#193456] text-xs font-bold px-2 py-1 rounded-full shadow-sm whitespace-nowrap">
                                          No video available
                                        </span>
                                      ) : isAdHoc && capability && !isTextOnlyFlow && !isLeadGenFlow ? (
                                        <MediaPills support={capability.support} isCardSelected={!!isSelected} />
                                      ) : null}
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <p
                                  className={`${
                                    isSelected ? "text-white" : "text-red-600"
                                  } text-sm mt-1`}
                                >
                                  Not Connected
                                </p>
                              )}
                            </div>
                          </Card>
                        );
                    })}
                </div>
            )}

            {/* Connect Accounts button */}
            {hasUnconnectedSelectedPlatforms && (
                <Button
                    onClick={() => navigate("/settings/connections")}
                    className="flex items-center justify-center mt-3"
                >
                    Manage Connections
                </Button>
            )}

            {/* Navigation Buttons */}
            {setCurrentStep && (
                <div className="flex justify-between mt-6">
                    <Button
                        variant="secondary"
                        type="button"
                        onClick={handleBackStep}
                    >
                        Back
                    </Button>
                    <Button
                        type="button"
                        onClick={handleNextStep}
                        disabled={isNextDisabled}
                    >
                        Next
                    </Button>
                </div>
            )}
        </div>
    );
};

export default ChoosePlatforms;